import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import axios from '../../utils/axios';

const ALL = '__ALL__';

const MARKETPLACES = [
  { value: '', label: 'Все' },
  { value: '1', label: 'Wildberries' },
  { value: '2', label: 'Ozon' },
  { value: '3', label: 'Yandex' },
];

const toggleInArray = (arr, val) => {
  const i = arr.indexOf(val);
  if (i === -1) return [...arr, val];
  const copy = arr.slice();
  copy.splice(i, 1);
  return copy;
};

const EMPTY_ARR = [];

export default function ProductFilters({
  value = {},
  onChange,
  showMp = true,
  showOrganization = true,
  showArticle = true,
  showExcludeMode = true,
  defaultMp = '',
  marketplaceOptions,
  marketplaceDisabled = false,
  filterMd = 2,
}) {
  const mpOptions = marketplaceOptions || MARKETPLACES;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  const mp = value.mp ?? defaultMp;
  const orgSel = value.orgSel || EMPTY_ARR;
  const orgMode = value.orgMode || 'include';
  const orgExcluded = value.orgExcluded || EMPTY_ARR;
  const gruppa = value.gruppa || EMPTY_ARR;
  const gruppaMode = value.gruppaMode || 'include';
  const gruppaExcluded = value.gruppaExcluded || EMPTY_ARR;
  const podgruppa = value.podgruppa || EMPTY_ARR;
  const podgruppaMode = value.podgruppaMode || 'include';
  const podgruppaExcluded = value.podgruppaExcluded || EMPTY_ARR;
  const selectedArticle = value.selectedArticle || null;

  const [orgs, setOrgs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [subgroups, setSubgroups] = useState([]);
  const [articles, setArticles] = useState([]);
  const [articleInputValue, setArticleInputValue] = useState('');
  const [articlesLoading, setArticlesLoading] = useState(false);

  // Stable emit: always reads latest value from ref, never triggers re-render loops
  const emit = useCallback((patch) => {
    const cur = valueRef.current;
    onChangeRef.current?.({ ...cur, ...patch });
  }, []);

  // Stable serialized keys for effect dependencies (avoids new-array-reference loops)
  const orgKey = orgMode === 'include' ? orgSel.join(',') : `!${orgExcluded.join(',')}`;
  const gruppaKey = gruppaMode === 'include' ? gruppa.join(',') : `!${gruppaExcluded.join(',')}`;

  // Load organizations — only depends on mp
  useEffect(() => {
    if (!showOrganization) return;
    let cancelled = false;
    (async () => {
      try {
        const params = {};
        if (mp) params.mp = mp;
        const resp = await axios.get('/filters/organizations', { params });
        if (cancelled) return;
        const newOrgs = resp.data.organizations || [];
        setOrgs(newOrgs);

        const cur = valueRef.current;
        const curOrgSel = cur.orgSel || EMPTY_ARR;
        const curOrgExcluded = cur.orgExcluded || EMPTY_ARR;
        const allowed = new Set(newOrgs);
        const nextOrgSel = curOrgSel.filter(o => allowed.has(o));
        const nextOrgExcluded = curOrgExcluded.filter(o => allowed.has(o));
        if (nextOrgSel.length !== curOrgSel.length || nextOrgExcluded.length !== curOrgExcluded.length) {
          emit({ orgSel: nextOrgSel, orgExcluded: nextOrgExcluded });
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [mp, showOrganization, emit]);

  // Load groups — depends on mp and org selection
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = {};
        if (mp) params.mp = mp;
        if (orgMode === 'include' && orgSel.length) params.organization = orgSel.join(',');
        const resp = await axios.get('/filters/groups', { params });
        if (cancelled) return;
        const newGroups = resp.data.groups || [];
        setGroups(newGroups);

        const cur = valueRef.current;
        const curGruppa = cur.gruppa || EMPTY_ARR;
        const curGruppaExcluded = cur.gruppaExcluded || EMPTY_ARR;
        const allowed = new Set(newGroups);
        const nextGruppa = curGruppa.filter(g => allowed.has(g));
        const nextGruppaExcluded = curGruppaExcluded.filter(g => allowed.has(g));
        if (nextGruppa.length !== curGruppa.length || nextGruppaExcluded.length !== curGruppaExcluded.length) {
          emit({ gruppa: nextGruppa, gruppaExcluded: nextGruppaExcluded });
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [mp, orgKey, emit]);

  // Load subgroups — depends on group filters. With no group selected,
  // the backend returns all subgroups available for the current scope.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = {};
        if (mp) params.mp = mp;
        if (orgMode === 'include' && orgSel.length) params.organization = orgSel.join(',');
        if (gruppaMode === 'include' && gruppa.length) {
          params.gruppa = gruppa.join(',');
        } else if (gruppaMode === 'exclude' && gruppaExcluded.length) {
          params.gruppa = gruppaExcluded.join(',');
          params.gruppa_exclude = '1';
        }
        const resp = await axios.get('/filters/subgroups', { params });
        if (cancelled) return;
        const newSubgroups = resp.data.subgroups || [];
        setSubgroups(newSubgroups);

        const cur = valueRef.current;
        const curPodgruppa = cur.podgruppa || EMPTY_ARR;
        const curPodgruppaExcluded = cur.podgruppaExcluded || EMPTY_ARR;
        const allowed = new Set(newSubgroups);
        const nextPodgruppa = curPodgruppa.filter(p => allowed.has(p));
        const nextPodgruppaExcluded = curPodgruppaExcluded.filter(p => allowed.has(p));
        if (nextPodgruppa.length !== curPodgruppa.length || nextPodgruppaExcluded.length !== curPodgruppaExcluded.length) {
          emit({ podgruppa: nextPodgruppa, podgruppaExcluded: nextPodgruppaExcluded });
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [gruppaKey, mp, orgKey, emit]);

  const podgruppaKey = podgruppa.join(',');

  // Load articles for autocomplete
  const loadArticles = useCallback(async (search = '') => {
    if (!showArticle) return;
    setArticlesLoading(true);
    try {
      const params = { mp: mp || '1', search };
      if (orgMode === 'include' && orgSel.length) params.organization = orgSel.join(',');
      if (gruppaMode === 'include' && gruppa.length) params.gruppa = gruppa.join(',');
      if (podgruppaMode === 'include' && podgruppa.length) params.podgruppa = podgruppa.join(',');
      const resp = await axios.get('/filters/articles', { params });
      setArticles(resp.data.articles || []);
    } catch (_) {
      setArticles([]);
    } finally {
      setArticlesLoading(false);
    }
  }, [mp, orgKey, orgMode, orgSel, gruppaKey, gruppaMode, podgruppaKey, podgruppaMode, showArticle]);

  useEffect(() => {
    if (showArticle) loadArticles();
  }, [loadArticles, showArticle]);

  // Cascade handlers: keep current child selections; pruning of invalid values
  // happens automatically after options are reloaded (see effects above).
  // Article is dropped on parent change because it is mp-specific by design.
  const handleMpChange = (newMp) => {
    emit({ mp: newMp, selectedArticle: null });
  };

  const handleOrgChange = (patch) => {
    emit({ ...patch, selectedArticle: null });
  };

  const handleGruppaChange = (patch) => {
    emit({ ...patch, selectedArticle: null });
  };

  const handlePodgruppaChange = (patch) => {
    emit({ ...patch, selectedArticle: null });
  };

  const renderMultiSelect = (label, items, selected, mode, excluded, onChange_) => (
    <TextField
      select label={label} value={selected}
      onChange={e => {
        const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
        if (Array.isArray(val) && val.includes(ALL)) {
          onChange_({ selected: [], mode: 'include', excluded: [] });
          return;
        }
        onChange_({ selected: val, mode: 'include', excluded });
      }}
      size="small" fullWidth
      SelectProps={{
        multiple: true,
        renderValue: () =>
          mode === 'exclude'
            ? (excluded.length ? `Все кроме: ${excluded.join(', ')}` : '')
            : (selected.join(', ') || 'Все'),
      }}
    >
      <MenuItem key="_all" value={ALL}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <em>Все</em>
          {mode === 'include' && selected.length === 0 && excluded.length === 0 && <CheckIcon fontSize="small" />}
        </Box>
      </MenuItem>
      {items.map(item => (
        <MenuItem key={item} value={item}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 1 }}>
            <span>{item}</span>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(mode === 'include' ? selected.includes(item) : !excluded.includes(item)) && <CheckIcon fontSize="small" />}
              {showExcludeMode && (
                <Button
                  size="small" variant="text"
                  onClick={e => {
                    e.stopPropagation();
                    if (mode !== 'exclude') {
                      onChange_({ selected: [], mode: 'exclude', excluded: [item] });
                    } else {
                      onChange_({ selected: [], mode: 'exclude', excluded: toggleInArray(excluded, item) });
                    }
                  }}
                  sx={{ minWidth: 0, color: 'text.secondary', textTransform: 'none', fontSize: 12, px: 0.5 }}
                >
                  кроме
                </Button>
              )}
            </Box>
          </Box>
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <Grid container spacing={2} alignItems="center">
      {showMp && (
        <Grid item xs={6} sm={4} md={1.5}>
          <TextField
            select label="Маркетплейс" value={mp}
            onChange={e => handleMpChange(e.target.value)}
            size="small" fullWidth disabled={marketplaceDisabled}
          >
            {mpOptions.map(m => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
      )}

      {showOrganization && (
        <Grid item xs={12} sm={6} md={2}>
          {renderMultiSelect('Организация', orgs, orgSel, orgMode, orgExcluded, ({ selected, mode, excluded }) => {
            handleOrgChange({ orgSel: selected, orgMode: mode, orgExcluded: excluded });
          })}
        </Grid>
      )}

      <Grid item xs={12} sm={6} md={filterMd}>
        {renderMultiSelect('Группа', groups, gruppa, gruppaMode, gruppaExcluded, ({ selected, mode, excluded }) => {
          handleGruppaChange({ gruppa: selected, gruppaMode: mode, gruppaExcluded: excluded });
        })}
      </Grid>

      <Grid item xs={12} sm={6} md={filterMd}>
        <TextField
          select label="Подгруппа" value={podgruppa}
          onChange={e => {
            const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
            if (Array.isArray(val) && val.includes(ALL)) {
              handlePodgruppaChange({ podgruppa: [], podgruppaMode: 'include', podgruppaExcluded: [] });
              return;
            }
            handlePodgruppaChange({ podgruppa: val, podgruppaMode: 'include' });
          }}
          size="small" fullWidth disabled={!subgroups.length}
          SelectProps={{
            multiple: true,
            renderValue: () =>
              podgruppaMode === 'exclude'
                ? (podgruppaExcluded.length ? `Все кроме: ${podgruppaExcluded.join(', ')}` : '')
                : (podgruppa.join(', ') || 'Все'),
          }}
        >
          <MenuItem key="_all_sub" value={ALL}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <em>Все</em>
              {podgruppaMode === 'include' && podgruppa.length === 0 && podgruppaExcluded.length === 0 && <CheckIcon fontSize="small" />}
            </Box>
          </MenuItem>
          {subgroups.map(sg => (
            <MenuItem key={sg} value={sg}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 1 }}>
                <span>{sg}</span>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {(podgruppaMode === 'include' ? podgruppa.includes(sg) : !podgruppaExcluded.includes(sg)) && <CheckIcon fontSize="small" />}
                  {showExcludeMode && (
                    <Button
                      size="small" variant="text"
                      onClick={e => {
                        e.stopPropagation();
                        if (podgruppaMode !== 'exclude') {
                          handlePodgruppaChange({ podgruppa: [], podgruppaMode: 'exclude', podgruppaExcluded: [sg] });
                        } else {
                          handlePodgruppaChange({ podgruppa: [], podgruppaMode: 'exclude', podgruppaExcluded: toggleInArray(podgruppaExcluded, sg) });
                        }
                      }}
                      sx={{ minWidth: 0, color: 'text.secondary', textTransform: 'none', fontSize: 12, px: 0.5 }}
                    >
                      кроме
                    </Button>
                  )}
                </Box>
              </Box>
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {showArticle && (
        <Grid item xs={12} sm={6} md={filterMd}>
          <Autocomplete
            size="small" options={articles}
            getOptionLabel={(option) => option?.article || ''}
            value={selectedArticle}
            onChange={(_, newValue) => emit({ selectedArticle: newValue })}
            inputValue={articleInputValue}
            onInputChange={(_, newInputValue, reason) => {
              setArticleInputValue(newInputValue);
              // Перезагружаем список только при реальном вводе пользователя.
              // При выборе значения (reason='reset') MUI подставляет полный артикул
              // в инпут — нельзя по нему искать, иначе список схлопнется до одного.
              if (reason === 'input') {
                if (newInputValue.length >= 2) loadArticles(newInputValue);
                else if (newInputValue.length === 0) loadArticles('');
              } else if (reason === 'clear') {
                loadArticles('');
              }
            }}
            loading={articlesLoading}
            slotProps={{
              popper: {
                placement: 'bottom-start',
                style: { width: 'fit-content' },
                sx: { minWidth: 280, maxWidth: 520 },
              },
            }}
            isOptionEqualToValue={(option, val) => option?.article === val?.article}
            renderInput={(params) => (
              <TextField
                {...params} label="Артикул" placeholder="Поиск..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {articlesLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.article}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>{option.article}</Typography>
                  <Typography variant="caption" color="text.secondary">{option.nmID}</Typography>
                </Box>
              </li>
            )}
            noOptionsText="Не найдено" loadingText="Загрузка..."
            clearOnBlur={false} freeSolo={false}
          />
        </Grid>
      )}
    </Grid>
  );
}

/**
 * Построить query-параметры из объекта фильтров для API-запросов
 */
export function buildFilterParams(filters = {}) {
  const params = {};
  const { mp, orgSel, orgMode, orgExcluded, gruppa, gruppaMode, gruppaExcluded, podgruppa, podgruppaMode, podgruppaExcluded, selectedArticle } = filters;

  if (mp) params.mp = mp;

  if (orgMode === 'include') {
    if (orgSel?.length) params.organization = orgSel.join(',');
  } else if (orgExcluded?.length) {
    params.organization = orgExcluded.join(',');
    params.organization_exclude = '1';
  }

  if (gruppaMode === 'include') {
    if (gruppa?.length) params.gruppa = gruppa.join(',');
  } else if (gruppaExcluded?.length) {
    params.gruppa = gruppaExcluded.join(',');
    params.gruppa_exclude = '1';
  }

  if (podgruppaMode === 'include') {
    if (podgruppa?.length) params.podgruppa = podgruppa.join(',');
  } else if (podgruppaExcluded?.length) {
    params.podgruppa = podgruppaExcluded.join(',');
    params.podgruppa_exclude = '1';
  }

  if (selectedArticle?.article) params.article = selectedArticle.article;

  return params;
}
